import addNewFiledIcon from 'Assets/images/add-new.svg';
import bhiveIcon from 'Assets/images/bhive-logo.svg';
import phoneIcon from 'Assets/images/call-start.svg';
import messageIcon from 'Assets/images/chat.svg';
import closeBtn from 'Assets/images/close-btn.svg';
import removeFiledIcon from 'Assets/images/remove-field.svg';
import { AvatarEditor } from 'Components/AvatarEditor';
import { EmailTypes, PhoneNumberTypes } from 'Constants/enums';
import {
  STORE_CONVERSATION,
  STORE_NOTIFICATION,
  STORE_PERSON,
  STORE_PHONE_CALL,
  STORE_PREFERENCE,
  STORE_UI,
} from 'Constants/stores';
import withRouter from 'Hocs/WithRouter';
import { inject, observer } from 'mobx-react';
import {
  IFormContact,
  IFormEmail,
  IFormPhoneNumber,
  TEmail,
  TPhoneNumber,
} from 'Models/ContactModelV2';
import { SourceType } from 'Models/SourceModelV2';
import { useNotificationStore } from 'Modules/notification/store';
import { useCallRouter } from 'Modules/softphone/hooks';
import { useUIStore } from 'Modules/ui/index.store';
import React from 'react';
import {
  Button,
  Dropdown,
  DropdownItemProps,
  DropdownProps,
  Input,
  Modal,
  TextArea,
} from 'semantic-ui-react';
import { isNullOrUndefined } from 'util';
import { formatNumberWithNationalCode } from 'Utils/phoneUtil';
import PhoneNumbersPopup from '../PhoneNumbersPopup';
import { Styled } from './index.styles';
import { AddNewContactProps, AddNewContactState } from './interfaces';
import { SourceInformation } from './SourceInformation';

const providers: DropdownItemProps[] = [
  {
    key: 'personal',
    image: { avatar: true, src: bhiveIcon },
    text: 'Personal contacts',
    value: 'personal' as SourceType,
  },
  {
    key: 'shared',
    image: { avatar: true, src: bhiveIcon },
    text: 'Organization contacts',
    value: 'shared' as SourceType,
  },
];

const emptyPhoneNumber: IFormPhoneNumber = {
  type: 'mobile',
  number: '',
  guid: 0,
};

const emptyEmail: IFormEmail = { type: 'work', address: '', guid: 0 };

const emptyFormData: IFormContact = {
  phoneNumbers: [{ ...emptyPhoneNumber }],
  emails: [{ ...emptyEmail }],
  firstName: '',
  lastName: '',
  organization: '',
  role: '',
  notes: '',
  id: '',
  sourceData: {
    type: 'personal',
  },
};

// Wrapper component that provides useCallRouter hook to the class component
const AddNewContactWrapper: React.FC<Omit<AddNewContactProps, 'callWithPerson'>> = (props) => {
  const { callWithPerson } = useCallRouter();
  return <AddNewContact {...props} callWithPerson={callWithPerson} />;
};

class AddNewContact extends React.Component<
  AddNewContactProps,
  AddNewContactState
> {
  constructor(props) {
    super(props);
    this.state = {
      formData: structuredClone(emptyFormData) as IFormContact,
      showRemoveModal: false,
      profilePicture: '',
      startedEditing: false,
      operationType: null,
      savingInProgress: false,
    };
  }

  addNotify = useNotificationStore.getState().addNotify;

  componentDidMount() {
    if (this.props.person.editContactDetails) {
      this.setState({ formData: this.mapDataFromProps() });
    }
  }

  componentDidUpdate() {
    if (
      !this.props.person?.editContactDetails &&
      this.state.formData.id !== ''
    ) {
      this.setState({
        ...this.state,
        formData: structuredClone(emptyFormData) as IFormContact,
      });
    }
  }

  handleChange = (e, d: DropdownProps) => {
    const { person } = this.props;

    const id = e.target.id || d.id;

    person.editContactDetails &&
      !this.state.startedEditing &&
      this.handleEditMode();

    if (id.includes('Type')) {
      const dataForUpdate =
        id === 'emailType'
          ? [...this.state.formData.emails]
          : [...this.state.formData.phoneNumbers];

      const fieldForUpdate = id === 'emailType' ? 'emails' : 'phoneNumbers';

      dataForUpdate[0].type = d.value as TEmail | TPhoneNumber;

      this.setState({
        formData: { ...this.state.formData, [fieldForUpdate]: dataForUpdate },
      });
    } else if (id === 'email' || id === 'phoneNumber') {
      const dataForUpdate =
        id === 'email'
          ? [...this.state.formData.emails]
          : [...this.state.formData.phoneNumbers];

      const fieldForUpdate = id === 'email' ? 'emails' : 'phoneNumbers';

      id === 'phoneNumber' && this.checkIfExtrInfoSidebar();

      id === 'email'
        ? ((dataForUpdate[0] as IFormEmail).address = e.target.value)
        : ((dataForUpdate[0] as IFormPhoneNumber).number = e.target.value);

      this.setState({
        formData: { ...this.state.formData, [fieldForUpdate]: dataForUpdate },
      });
    } else if (id === 'selectedSource') {
      this.setState({
        formData: {
          ...this.state.formData,
          sourceData: {
            ...this.state.formData.sourceData,
            type: d.value as SourceType,
          },
          [id]: d.value,
        },
      });
    } else {
      this.setState({
        formData: { ...this.state.formData, [id]: e.target.value || d.value },
      });
    }
  };

  checkIfExtrInfoSidebar = () => {
    const { person } = this.props;
    // we have to close info sidebar if number is changed
    useUIStore.getState().entitySidePanelInfo?.entityType === 'extrContact' &&
      useUIStore.getState().resetEntitySidePanel();
  };

  handleChangeAdditionalFields = (e) => {
    const { startedEditing, formData } = this.state;

    const { person } = this.props;

    person.editContactDetails && !startedEditing && this.handleEditMode();

    const isEmail = e.target.id.includes('email');
    const index = e.target.id.split('-')[1];

    const fieldForUpdate = isEmail
      ? [...formData.emails]
      : [...formData.phoneNumbers];

    isEmail
      ? ((fieldForUpdate[index] as IFormEmail).address = e.target.value)
      : ((fieldForUpdate[index] as IFormPhoneNumber).number = e.target.value);

    isEmail
      ? this.setState({
          formData: { ...formData, emails: fieldForUpdate as IFormEmail[] },
        })
      : this.setState({
          formData: {
            ...formData,
            phoneNumbers: fieldForUpdate as IFormPhoneNumber[],
          },
        });

    !isEmail && this.checkIfExtrInfoSidebar();
  };

  handleEditMode = () => {
    if (!this.state.startedEditing) {
      this.setState({ startedEditing: true });
      this.props.person.setIsEditingContact(true);
    }
  };

  handleChangeType = (e, d) => {
    const { startedEditing, formData } = this.state;

    const { person } = this.props;

    person.editContactDetails && !startedEditing && this.handleEditMode();

    const index = d.id.split('-')[1];

    const fieldForUpdate = d.id.includes('email')
      ? [...formData.emails]
      : [...formData.phoneNumbers];

    fieldForUpdate[index].type = d.value;

    d.id.includes('email')
      ? this.setState({
          formData: { ...formData, emails: fieldForUpdate as IFormEmail[] },
        })
      : this.setState({
          formData: {
            ...formData,
            phoneNumbers: fieldForUpdate as IFormPhoneNumber[],
          },
        });
  };

  insertNewField = (fieldType: 'Email' | 'PhoneNumber') => () => {
    const { formData } = this.state;

    fieldType === 'Email'
      ? this.setState({
          formData: {
            ...formData,
            emails: [
              ...formData.emails,
              {
                ...emptyEmail,
                guid: new Date().getMilliseconds(),
              },
            ],
          },
        })
      : this.setState({
          formData: {
            ...formData,
            phoneNumbers: [
              ...formData.phoneNumbers,
              {
                ...emptyPhoneNumber,
                guid: new Date().getMilliseconds(),
              },
            ],
          },
        });
  };

  removeFieldFromList = (field: string, guid: number) => () => {
    const newList =
      field === 'Email'
        ? this.state.formData.emails.filter((item) => item.guid !== guid)
        : this.state.formData.phoneNumbers.filter((item) => item.guid !== guid);

    field === 'Email'
      ? this.setState({
          formData: { ...this.state.formData, emails: newList as IFormEmail[] },
          startedEditing: true,
        })
      : this.setState({
          formData: {
            ...this.state.formData,
            phoneNumbers: newList as IFormPhoneNumber[],
          },
          startedEditing: true,
        });
  };

  checkIsFormValid = (contact: IFormContact) => {
    const {
      person: { setErrorNewContact },
    } = this.props;
    const phoneNumEmpty = contact.phoneNumbers?.find(
      (phone) => !phone.type || !phone.number || phone.number === '+'
    );
    const invalidFirstName =
      !contact.firstName ||
      isNullOrUndefined(contact.firstName) ||
      contact.firstName.trim().length === 0;
    const invalidLastName =
      !contact.lastName ||
      isNullOrUndefined(contact.lastName) ||
      contact.lastName.trim().length === 0;
    const invalidOrganization =
      !contact.organization ||
      isNullOrUndefined(contact.organization) ||
      contact.organization.trim().length === 0;
    if (!contact.sourceData.type) {
      setErrorNewContact({
        show: true,
        message: 'Save contact to field is required',
        field: 'selectedSource',
      });
      return false;
    }

    if ((invalidFirstName || invalidLastName) && invalidOrganization) {
      invalidLastName &&
        setErrorNewContact({
          show: true,
          message: 'Last name field is required',
          field: 'lastName',
        });
      invalidFirstName &&
        setErrorNewContact({
          show: true,
          message: 'First name field is required',
          field: 'firstName',
        });
      return false;
    }
    if (invalidOrganization && (invalidFirstName || invalidLastName)) {
      setErrorNewContact({
        show: true,
        message: 'Organization field is required',
        field: 'organization',
      });
      return false;
    }

    if (phoneNumEmpty) {
      const index = contact.phoneNumbers.indexOf(phoneNumEmpty);
      setErrorNewContact({
        show: true,
        message: 'Phone number and phone type field are required',
        field: `phoneField${index}`,
      });
      return false;
    } else if (contact.phoneNumbers.length > 0) {
      let valid = true;
      contact.phoneNumbers.forEach((phone, index) => {
        try {
          if (
            !this.props.person.checkPhoneNumberValidation(phone.number) ||
            this.props.person.phoneNumInvalid(phone.number)
          ) {
            setErrorNewContact({
              show: true,
              message: 'Phone number is not in a correct format',
              field: `phoneField${index}`,
            });
            valid = false;
          }
        } catch (err) {
          setErrorNewContact({
            show: true,
            message: 'Phone number is not in a correct format',
            field: `phoneField${index}`,
          });
          valid = false;
        }
      });
      return valid;
    }
    return true;
  };

  handleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { person, navigate } = this.props;
    const isBHiveSource =
      typeof this.state.formData['selectedSource'] !== 'number';

    const allEmails: IFormEmail[] = this.state.formData.emails
      .map((item) => ({ type: item?.type, address: item?.address }))
      .filter((item) => item.type && item.address);

    const allPhones: IFormPhoneNumber[] = this.state.formData.phoneNumbers
      .map((item) => ({
        type: item?.type,
        number: person.removeAllSpecialCaracters(item.number),
      }))
      .filter((item) => item.number);

    const formatedData: IFormContact = {
      ...this.state.formData,
      emails: allEmails,
      phoneNumbers: allPhones,
    };

    if (
      !isBHiveSource ||
      (!formatedData.avatarUrl && !this.state.profilePicture)
    ) {
      formatedData.avatarUrl = undefined;
    }

    const isValid = this.checkIsFormValid(formatedData);
    if (isValid) {
      this.setState({ savingInProgress: true });
      //external source, image should not be uploaded to the s3
      formatedData.sourceData.accountId &&
        person.setContactPictureLocally(null);
      const resp =
        person.editContactDetails && !person.isAddingContact
          ? await person.updateContact(formatedData)
          : await person.addNewContact(formatedData);
      if (resp) {
        this.addNotify({
          title: 'Contact created successfully',
          message: `${resp?.name} was successfully created.`,
          action: {
            label: 'View contact',
            onClick: () => {
              person.setEditContact(resp);
              navigate('/addressBook/contact');
            },
          },
          autoDismiss: true,
          dismissible: true,
        });
        navigate('/addressBook/contacts');
        this.closeEditingMode();
      } else {
        this.setState({ savingInProgress: false });
      }
    } else {
      return false;
    }
  };

  makeCall = (e) => {
    const { callWithPerson, person } = this.props;
    e.stopPropagation();
    if (person.editContactDetails.phoneNumbers.length > 1) {
      this.setState({ operationType: 'call' });
    } else if (person.editContactDetails?.phoneNumbers.length === 1) {
      const phone = person.editContactDetails?.phoneNumbers[0].number;
      void callWithPerson(undefined, phone);
    }
  };

  handleOpenChat = async (e) => {
    const { conversation, person, navigate } = this.props;
    e.stopPropagation();
    if (person.editContactDetails?.phoneNumbers.length > 1) {
      this.setState({ operationType: 'message' });
    } else if (person.editContactDetails?.phoneNumbers.length === 1) {
      const phone = person.editContactDetails?.phoneNumbers[0].number;
      const resp = await conversation.loadOrCreateConversationWithPost(
        null,
        phone
      );
      navigate(`/chat/conversations/${resp.data.id}/menu`);
    }
  };

  clearOperationType = () => this.setState({ operationType: null });

  closeEditingMode = () => {
    const { person } = this.props;
    person.setEditContact(null);
    person.setIsEditingContact(false);
    this.setState({
      startedEditing: false,
      formData: structuredClone(emptyFormData) as IFormContact,
      savingInProgress: false,
    });
    person.setEditingLeavePopup({ show: false, wantedUrl: '' });
    if (person.isAddingContact) {
      person.setIsAddingContact(false);
    }
  };

  mapDataFromProps = () => {
    const editContactData = this.props.person.editContactDetails;

    const formData: IFormContact = {
      id: editContactData.id,
      sourceData: {
        type: editContactData.sourceData?.type,
        accountId: editContactData.sourceData?.accountId,
      },
      firstName: editContactData.firstName,
      lastName: editContactData.lastName,
      organization: editContactData.organization,
      role: editContactData.role,
      phoneNumbers: editContactData.phoneNumbers?.length
        ? editContactData.phoneNumbers.map((phoneNumber, index) => ({
            ...phoneNumber,
            guid: index,
          }))
        : [{ ...emptyPhoneNumber }],
      emails: editContactData.emails?.length
        ? editContactData.emails.map((email, index) => ({
            ...email,
            guid: index,
          }))
        : [{ ...emptyEmail }],
      notes: editContactData.notes || '',
    };

    return formData;
  };

  toggleRemoveModal = () =>
    this.setState({ showRemoveModal: !this.state.showRemoveModal });

  handleRedirectOnEdit = () => {
    const { person, navigate } = this.props;
    const url = person.showEditingLeavePopup.wantedUrl;
    person.setContactPictureLocally(null);
    this.closeEditingMode();
    navigate(url);
  };

  toggleEditLeaveModal = () => {
    const {
      person: { setEditingLeavePopup, showEditingLeavePopup },
    } = this.props;
    setEditingLeavePopup({
      show: !showEditingLeavePopup,
      wantedUrl: showEditingLeavePopup.wantedUrl,
    });
  };

  handleRemoveContact = async () => {
    const { person, navigate } = this.props;
    if (person.isEditingContact) {
      person.setIsEditingContact(false);
    }
    await person.removeContact(person.editContactDetails.id);
    this.setState({ showRemoveModal: false });
    navigate(-1);
  };

  handleSaveAndContinue = (e) => {
    this.toggleEditLeaveModal();
    this.handleSave(e);
  };

  RemoveModal = () => {
    const { editContactDetails } = this.props.person;
    const firstName = editContactDetails ? editContactDetails.firstName : '';
    const lastName = editContactDetails ? editContactDetails.lastName : '';
    return (
      <Modal
        size="small"
        id="remove-modal"
        className="remove-modal"
        open={this.state.showRemoveModal}
        dimmer="blurring"
        closeOnDimmerClick={true}
        centered={true}
        onClose={this.toggleRemoveModal}
      >
        <Modal.Content>
          <div className="text-center">
            You are about to remove <b>{`${firstName} ${lastName}`}</b> from
            contacts. <br />
            Are you sure?
          </div>
          <div className="buttons semantic-flex-row semantic-flex-align-items-center semantic-flex-justify-center">
            <Button primary onClick={this.toggleRemoveModal}>
              No
            </Button>
            <Button secondary onClick={this.handleRemoveContact}>
              Yes
            </Button>
          </div>
        </Modal.Content>
        <img src={closeBtn} onClick={this.toggleRemoveModal} />
      </Modal>
    );
  };

  EditLeaveModal = () => {
    return (
      <Modal
        size="small"
        id="remove-modal"
        className="remove-modal"
        open={this.props.person.showEditingLeavePopup.show}
        dimmer="blurring"
        closeOnDimmerClick={true}
        centered={true}
        onClose={this.toggleEditLeaveModal}
      >
        <Modal.Content>
          <div className="text-center">
            The edited contact details are not saved yet.
            <br />
            Would you like to continue without saving?
          </div>
          <div className="buttons semantic-flex-row semantic-flex-align-items-center semantic-flex-justify-center">
            <Button primary onClick={this.handleSaveAndContinue}>
              SAVE AND CONTINUE
            </Button>
            <Button secondary onClick={this.handleRedirectOnEdit}>
              Yes
            </Button>
          </div>
        </Modal.Content>
        <img src={closeBtn} onClick={this.toggleEditLeaveModal} />
      </Modal>
    );
  };

  EditSection = () => {
    const { person, conversation, callWithPerson } = this.props;
    return (
      <div className="edit-contact-section">
        <div className="buttons">
          {person.editContactDetails &&
            !person.isAddingContact &&
            this.state.operationType && (
              <PhoneNumbersPopup
                phoneNumbers={person.editContactDetails.phoneNumbers}
                callWithPerson={callWithPerson}
                loadOrCreateConversationWithPost={
                  conversation.loadOrCreateConversationWithPost
                }
                operationType={this.state.operationType}
              />
            )}
          <span className="icons" onClick={this.handleOpenChat}>
            <img src={messageIcon} />
          </span>
          <span className="icons left-8" onClick={this.makeCall}>
            <img src={phoneIcon} />
          </span>
          <span className="text-section" onClick={this.toggleRemoveModal}>
            DELETE CONTACT
          </span>
        </div>
      </div>
    );
  };

  redirectToPrevPage = () => {
    const { navigate, location, person } = this.props;
    if (location.state?.from === '/contacts') {
      if (person.isEditingContact) {
        person.setEditingLeavePopup({
          show: true,
          wantedUrl: '/addressBook/contacts',
        });
        return;
      }
      navigate('/addressBook/contacts');
      return;
    }

    navigate('/addressBook/contacts');
    this.closeEditingMode();
  };

  fileImportEvent = async (e: any): Promise<void> => {
    const { person } = this.props;
    const files = e.clipboardData?.files || e.target?.files || [];
    if (!files) {
      return;
    }
    const importedFiles: File[] = Array.from(files);
    person.setContactPictureLocally(importedFiles[0]);
    this.setState({
      ...this.state,
      profilePicture: URL.createObjectURL(importedFiles[0]),
      formData: { ...this.state.formData },
    });
    person.editContactDetails &&
      !this.state.startedEditing &&
      this.handleEditMode();
  };

  setDefaultOnOpen = (e, d) => {
    if (d.id === 'phoneNumberType') {
      const index = this.state.formData.phoneNumbers.length - 1;
      const phone = this.state.formData.phoneNumbers[index];
      if (!phone.type) {
        this.setState({
          formData: {
            ...this.state.formData,
            phoneNumbers: [
              ...this.state.formData.phoneNumbers.slice(0, index),
              { ...phone, type: 'mobile' },
            ],
          },
        });
      }
    } else if (d.id === 'emailType') {
      const index = this.state.formData.emails.length - 1;
      const email = this.state.formData.emails[index];
      if (!email.type) {
        this.setState({
          formData: {
            ...this.state.formData,
            emails: [
              ...this.state.formData.emails.slice(0, index),
              { ...email, type: 'work' },
            ],
          },
        });
      }
    }
  };

  render() {
    const { person, ui, navigate } = this.props;
    const { formData, profilePicture, startedEditing } = this.state;
    const phoneNumber = formData?.phoneNumbers[0]?.number || '';
    const formatedPhoneNumber = formatNumberWithNationalCode(phoneNumber) || '';
    const showImageUpload = typeof formData['selectedSource'] !== 'number'; // selectedSource for external providers are numbers

    const contactSource = person.allSources.find(
      (source) => source.id.toString() === formData?.sourceData.accountId
    );

    const isProviderStale =
      person.editContactDetails &&
      !person.isAddingContact &&
      contactSource?.sourceData.type;

    const isSourceDropdownVisible =
      !person.editContactDetails?.sourceData?.type ||
      person.editContactDetails.sourceData.type === 'personal';

    return (
      <div className="new-contact" onClick={this.clearOperationType}>
        <div className="semantic-flex-row">
          <Styled.AvatarEditorContainer>
            <AvatarEditor
              size="small"
              fileImportEvent={this.fileImportEvent}
              pictureUrl={
                profilePicture || person.editContactDetails?.avatarUrl
              }
              name={person.editContactDetails?.name}
              isEditable={showImageUpload}
              testid=""
            />
          </Styled.AvatarEditorContainer>

          {person.editContactDetails && !person.isAddingContact && (
            <this.EditSection />
          )}
          <div
            className={`buttons semantic-flex-row header-buttons ${
              person.editContactDetails ? 'left-30' : ''
            }`}
          >
            <Button
              primary
              type="button"
              onClick={this.handleSave}
              disabled={
                (person.editContactDetails &&
                  !startedEditing &&
                  !person.isAddingContact) ||
                this.state.savingInProgress
              }
            >
              {!person.editContactDetails || person.isAddingContact
                ? 'ADD CONTACT'
                : 'SAVE'}
            </Button>
            <Button onClick={this.redirectToPrevPage} secondary>
              CANCEL
            </Button>
          </div>
        </div>

        {isProviderStale && <SourceInformation source={contactSource} />}

        <div className="form semantic-flex-row">
          <div className="flex-evenly">
            {isSourceDropdownVisible && (
              <Dropdown
                data-private
                id="selectedSource"
                selectOnBlur={false}
                value={formData.sourceData.type || ''}
                className={`form-input ${
                  person.newContactErrors.field === 'selectedSource'
                    ? 'error-field'
                    : ''
                }`}
                placeholder="Save Contact To"
                fluid
                selection
                options={providers}
                onChange={this.handleChange}
              />
            )}
            <Input
              data-private
              className={`form-input ${
                person.newContactErrors.field === 'firstName'
                  ? 'error-field'
                  : ''
              }`}
              value={formData?.firstName || ''}
              placeholder="First Name"
              id="firstName"
              onChange={this.handleChange}
            />
            <Input
              data-private
              className={`form-input ${
                person.newContactErrors.field === 'lastName'
                  ? 'error-field'
                  : ''
              }`}
              value={formData?.lastName || ''}
              placeholder="Last Name"
              id="lastName"
              onChange={this.handleChange}
            />
            <Input
              data-private
              className={`form-input ${
                person.newContactErrors.field === 'organization'
                  ? 'error-field'
                  : ''
              }`}
              value={formData?.organization || ''}
              placeholder="Organization (Company)"
              id="organization"
              onChange={this.handleChange}
            />
            <Input
              data-private
              className="form-input"
              value={formData?.role || ''}
              placeholder="Role"
              id="role"
              onChange={this.handleChange}
            />
            <div className="semantic-flex-row">
              <Input
                data-private
                className={`form-input ${
                  person.newContactErrors.field === 'phoneField0'
                    ? 'error-field'
                    : ''
                }`}
                value={formatedPhoneNumber || formData?.phoneNumbers[0]?.number}
                placeholder="Phone number"
                id="phoneNumber"
                onChange={this.handleChange}
              />
              <Dropdown
                data-private
                id="phoneNumberType"
                className={`form-input type-input ${
                  person.newContactErrors.field === 'phoneField0'
                    ? 'error-field'
                    : ''
                }`}
                value={formData?.phoneNumbers[0]?.type || ''}
                placeholder="Type"
                fluid
                selection
                options={PhoneNumberTypes}
                onChange={this.handleChange}
                onOpen={this.setDefaultOnOpen}
              />
              <img
                src={addNewFiledIcon}
                onClick={this.insertNewField('PhoneNumber')}
              />
            </div>
            {formData.phoneNumbers.slice(1)?.map((phone, index) => {
              const realIndex = index + 1;

              return (
                <div
                  className="semantic-flex-row"
                  key={`phoneNumberSection-${phone.guid}`}
                >
                  <Input
                    data-private
                    className={`form-input ${
                      person.newContactErrors.field === `phoneField${realIndex}`
                        ? 'error-field'
                        : ''
                    }`}
                    value={
                      formatNumberWithNationalCode(phone.number) ||
                      phone.number ||
                      ''
                    }
                    placeholder="Phone number"
                    id={`phoneNumber-${realIndex}`}
                    name={`phoneNumber-${realIndex}`}
                    onChange={this.handleChangeAdditionalFields}
                  />
                  <Dropdown
                    id={`phoneNumberType-${realIndex}`}
                    className={`form-input type-input ${
                      person.newContactErrors.field === `phoneField${realIndex}`
                        ? 'error-field'
                        : ''
                    }`}
                    name={`phoneNumberType-${realIndex}`}
                    value={phone.type || ''}
                    placeholder="Type"
                    fluid
                    selection
                    options={PhoneNumberTypes}
                    onChange={this.handleChangeType}
                  />
                  <img
                    src={removeFiledIcon}
                    onClick={this.removeFieldFromList(
                      'PhoneNumber',
                      phone.guid
                    )}
                  />
                </div>
              );
            })}
            <div className="semantic-flex-row">
              <Input
                data-private
                className={`form-input ${
                  person.newContactErrors.field === 'emailField0'
                    ? 'error-field'
                    : ''
                }`}
                type="email"
                value={formData?.emails[0]?.address || ''}
                placeholder="Email"
                id="email"
                onChange={this.handleChange}
              />
              <Dropdown
                id="emailType"
                className={`form-input type-input ${
                  person.newContactErrors.field === 'emailField0'
                    ? 'error-field'
                    : ''
                }`}
                value={formData?.emails[0]?.type || ''}
                placeholder="Type"
                fluid
                selection
                options={EmailTypes}
                onChange={this.handleChange}
              />
              <img
                src={addNewFiledIcon}
                onClick={this.insertNewField('Email')}
              />
            </div>
            {formData.emails.slice(1)?.map((email, index) => {
              const realIndex = index + 1;

              return (
                <div
                  className="semantic-flex-row"
                  key={`emailSection-${email.guid}`}
                >
                  <Input
                    type="email"
                    className={`form-input ${
                      person.newContactErrors.field === `emailField${realIndex}`
                        ? 'error-field'
                        : ''
                    }`}
                    value={email.address || ''}
                    placeholder="Email"
                    id={`email-${realIndex}`}
                    name={`email-${realIndex}`}
                    onChange={this.handleChangeAdditionalFields}
                  />
                  <Dropdown
                    id={`emailType-${realIndex}`}
                    name={`emailType-${realIndex}`}
                    className={`form-input type-input ${
                      person.newContactErrors.field === `emailField${realIndex}`
                        ? 'error-field'
                        : ''
                    }`}
                    value={email.type || ''}
                    placeholder="Type"
                    fluid
                    selection
                    options={EmailTypes}
                    onChange={this.handleChangeType}
                  />
                  <img
                    src={removeFiledIcon}
                    onClick={this.removeFieldFromList('Email', email.guid)}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex-evenly">
            <TextArea
              data-private
              id="notes"
              rows={8}
              className="form-input border"
              value={formData?.notes || ''}
              placeholder="Notes"
              onChange={this.handleChange}
            />
          </div>
        </div>
        <this.RemoveModal />
        <this.EditLeaveModal />
      </div>
    );
  }
}

export default inject(
  STORE_UI,
  STORE_PREFERENCE,
  STORE_NOTIFICATION,
  STORE_CONVERSATION,
  STORE_PERSON,
  STORE_PHONE_CALL
)(withRouter(observer(AddNewContactWrapper)));
